import { useQuery } from '@tanstack/react-query';

import {
  AUTHORIZATION_ROLE_LABELS,
  PERMISSION_FEATURE_LABELS,
  PERMISSION_GRID,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { QueryState } from '../../components/QueryState.js';
import { personName, usePeople } from '../../components/conversations/hooks.js';

/** Plain English for every recorded action. A raw enum in a feed a producer
 *  reads weeks later is a lookup exercise, not a record. The Overview's recent
 *  activity reads the same words. */
export const ACTION_LABEL: Record<string, string> = {
  PROJECT_CREATED: 'created the project',
  LIFECYCLE_PHASE_CHANGED: 'moved the project to a new phase',
  HEALTH_OVERRIDDEN: 'set the project health by hand',
  HEALTH_OVERRIDE_CLEARED: 'returned health to the computed value',
  SELECTION_CONFIRMED: 'confirmed a primary location',
  REQUEST_CREATED: 'raised a request',
  MEMBER_INVITED: 'invited someone to the project',
  INVITATION_REVOKED: 'revoked an invitation',
  MEMBER_JOINED: 'joined the project',
  JOIN_LINK_CREATED: 'created an invitation link',
  JOIN_LINK_REPLACED: 'replaced the invitation link',
  JOIN_LINK_REVOKED: 'revoked the invitation link',
  JOIN_REQUESTED: 'asked to join through the invitation link',
  JOIN_REQUEST_APPROVED: 'approved a request to join',
  JOIN_REQUEST_REJECTED: 'rejected a request to join',
  MEMBER_ROLE_CHANGED: 'changed what someone can do',
  ROLE_PERMISSION_CHANGED: 'changed what a role can do here',
  SETUP_SCENES_OVERVIEW_COMPLETED: 'went through the scene overview',
  SETUP_SCENES_CONFIRMED: 'confirmed the scene list',
  SETUP_BREAKDOWN_CONTINUED: 'went on to locations with the breakdown still open',
  SETUP_COMPLETED: 'finished setting up the project',
};

/**
 * The detail, where the payload carries one worth reading.
 *
 * `ROLE_PERMISSION_CHANGED` is the entry this exists for: the permission table
 * holds only the CURRENT answer, so a production that finds a role can suddenly
 * do something has nowhere else to learn who widened it. "changed what a role
 * can do here" without saying which role and which permission sends them to ask
 * somebody, which is exactly the lookup a record is supposed to save.
 */
function detail(action: string, payload: Record<string, unknown> | null): string | null {
  if (action === 'SETUP_BREAKDOWN_CONTINUED') return breakdownContinuedDetail(payload);
  if (action !== 'ROLE_PERMISSION_CHANGED' || payload === null) return null;

  const role = typeof payload.role === 'string' ? payload.role : null;
  const permission = typeof payload.permission === 'string' ? payload.permission : null;
  const state = typeof payload.state === 'string' ? payload.state : null;
  if (role === null || permission === null || state === null) return null;

  const verb =
    state === 'GRANTED' ? 'gave' : state === 'REVOKED' ? 'took away' : 'reset to the default for';

  // Labels come from the shared catalogue so this reads as a job and a job's
  // act, never as two database values.
  const roleLabel =
    AUTHORIZATION_ROLE_LABELS[role as keyof typeof AUTHORIZATION_ROLE_LABELS] ?? role;
  const cell = PERMISSION_GRID.find((c) => c.permission === permission);
  const permissionLabel = cell ? `${PERMISSION_FEATURE_LABELS[cell.feature]} · ${cell.label}` : permission;

  return `${verb} ${roleLabel}: ${permissionLabel}`;
}

/**
 * How much was left when somebody moved on. The live counts change the moment
 * anyone decides the next requirement; the entry keeps what was true when the
 * choice was made, which is the part a later reader cannot reconstruct.
 */
function breakdownContinuedDetail(payload: Record<string, unknown> | null): string | null {
  if (payload === null) return null;

  const undecided = typeof payload.undecidedRequirements === 'number' ? payload.undecidedRequirements : null;
  const uncovered =
    typeof payload.scenesWithoutRequirements === 'number' ? payload.scenesWithoutRequirements : null;
  if (undecided === null || uncovered === null) return null;

  const parts = [
    undecided > 0 ? `${String(undecided)} requirement${undecided === 1 ? '' : 's'} undecided` : null,
    uncovered > 0 ? `${String(uncovered)} scene${uncovered === 1 ? '' : 's'} without one` : null,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? null : parts.join(', ');
}

export function ActivityTab({ projectId }: { projectId: string }) {
  const entries = useQuery({
    queryKey: ['activity', projectId],
    queryFn: () => api.listActivity(projectId),
  });
  // Names from the platform's directory; "Member" when one cannot be read.
  const people = usePeople(projectId);

  return (
    <div data-testid="activity-tab">
      <p className="dh" style={{ fontSize: 22 }}>
        Activity
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6, maxWidth: '68ch' }}>
        Every decision on this project, in order. Append-only — entries are never
        edited or removed, so the record of what happened stays the record.
      </p>

      <QueryState query={entries} subject="the activity log" testId="activity" />

      {entries.data && entries.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }} data-testid="activity-empty">
          Nothing recorded yet.
        </p>
      ) : null}

      <ol style={{ listStyle: 'none', margin: '18px 0 0', padding: 0, display: 'grid', gap: 1, background: 'var(--color-divider)', border: '1px solid var(--color-divider)' }}>
        {entries.data?.map((e) => (
          <li
            key={e.id}
            data-testid={`activity-${e.id}`}
            style={{ background: 'var(--color-bg)', padding: '12px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>
              {e.actorUserId ? personName(people.data?.people, e.actorUserId, null) : 'System'}
            </span>
            <span style={{ fontSize: 12.5 }}>
              {ACTION_LABEL[e.action] ?? e.action.toLowerCase().replace(/_/g, ' ')}
              {detail(e.action, e.payload) === null ? null : (
                <span className="mm" data-testid={`activity-detail-${e.id}`}>
                  {' '}
                  — {detail(e.action, e.payload)}
                </span>
              )}
            </span>
            <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              {new Date(e.createdAt).toLocaleString()}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
